import Button from "atoms/buttons/button";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { ButtonStyles } from "atoms/constants/button-styles";
import { Icons } from "atoms/constants/icons";
import { ParagraphSizes } from "atoms/constants/paragraph-sizes";
import Icon from "atoms/icons/icon";
import Paragraph from "atoms/typography/paragraph";
import { CodeReferenceBlockHighlightRecord } from "models/view-models/code-reference-block-highlights/code-reference-block-highlight-record";
import { ConfirmationModal } from "molecules/modals/confirmation-modal";
import RichTextArea from "molecules/rich-text-area/rich-text-area";
import moment from "moment";
import { HighlightProperties } from "organisms/code-reference-block-editor/code-reference-preview";
import CodeReferenceHighlightEditModalContents from "organisms/highlight-modal/code-reference-highlight-edit-modal-contents";
import usePageActionPermission, {
    PageAction,
} from "pages/notebooks/use-page-action-permission";
import React, { RefObject, useMemo, useRef, useState } from "react";
import { HighlightColor } from "utilities/enumerations/highlight-color";
import HighlightUtils from "utilities/highlight-utils";
import useCurrentPage from "utilities/hooks/domain/notebooks/pages/use-current-page";
import useModalActions from "utilities/hooks/use-modal-actions";
import { t } from "utilities/localization-utils";
import StringUtils from "utilities/string-utils";
import useActiveHighlightModal, {
    HighlightEditModalType,
} from "./useActiveHighlightModal";
import { BrowserUtils } from "utilities/browser-utils";
import Modal from "molecules/modals/modal";
import { ModalTypes } from "molecules/constants/modal-types";

interface CodeReferenceHighlightViewModalProps {
    blockRefElement?: RefObject<HTMLDivElement>;
    highlights: CodeReferenceBlockHighlightRecord[];
    editType: HighlightEditModalType;
    onSave: (highlightMeta: HighlightProperties) => Promise<void>;
    onColorChange: (newColor: HighlightColor) => void;
    onClose: () => void;
    onDelete: (highlightId: number | undefined) => Promise<void>;
    coordinates?: { x: number; y: number };
}
const CSS_CLASS_NAME = "c-code-reference-highlight-view-modal";
const CodeReferenceHighlightViewModal = ({
    blockRefElement,
    highlights,
    editType,
    onClose,
    onColorChange,
    onSave,
    onDelete,
    coordinates,
}: CodeReferenceHighlightViewModalProps) => {
    const [activeHighlightIndex, setActiveHighlightIndex] = useState<number>(0);

    const {
        isOpen: isDeleteConfirmationModalOpen,
        handleClose: handleDeleteConfirmationModalClose,
        handleOpen: handleDeleteConfirmationModalOpen,
    } = useModalActions();
    const {
        setActiveEditModal,
        closeActiveEditModal,
        activeEditHighlightModal,
    } = useActiveHighlightModal();
    const { currentPage } = useCurrentPage();
    const { can } = usePageActionPermission(currentPage);
    const dialogRef = useRef<HTMLDialogElement>(null);
    const showToggles = !coordinates; // If there's coordinates, it means this is being showm after a click

    const activeHighlight = Number.isFinite(activeHighlightIndex)
        ? highlights[activeHighlightIndex]
        : undefined;
    const highlightCount = highlights.length ?? 0;

    const handleNextHighlight = (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        e.stopPropagation();
        setActiveHighlightIndex((prev) => prev + 1);
    };
    const handlePreviousHighlight = (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        e.stopPropagation();
        setActiveHighlightIndex((prev) => prev - 1);
    };

    const handleEditClick = () => {
        setActiveEditModal(editType);
    };

    const handleCloseEditor = () => {
        closeActiveEditModal();
    };

    const handleOnSaveClick = (highlightMeta: HighlightProperties) => {
        onSave(highlightMeta);
    };

    const handleConfirmDelete = () => {
        handleDeleteConfirmationModalClose();
        onDelete(activeHighlight?.id);
    };

    const highlighedText =
        activeHighlight && blockRefElement?.current
            ? HighlightUtils.buildRangeFromHighlightRecord(
                  blockRefElement.current,
                  activeHighlight
              ).toString()
            : "";

    const isMouseDevice = useMemo(() => BrowserUtils.isMouseDevice(), []);
    const canGoNext = activeHighlightIndex + 1 < highlights.length;

    const canGoPrev = activeHighlightIndex - 1 >= 0;
    const note = activeHighlight?.note.length
        ? activeHighlight.note
        : t("add-a-note");

    // Always says updated - why?
    const timestamp =
        activeHighlight?.updatedOn === activeHighlight?.createdOn
            ? `${StringUtils.upperFirst(t("added"))} ${moment(activeHighlight?.createdOn).fromNow()}`
            : `${StringUtils.upperFirst(t("updated"))} ${moment(activeHighlight?.updatedOn).fromNow()}`;

    const styles = () => {
        if (!coordinates) {
            return;
        }
        const minX = 0,
            minY = 0;
        const maxY =
            blockRefElement?.current?.clientHeight! -
            dialogRef?.current?.offsetHeight!;
        const maxX =
            blockRefElement?.current?.clientWidth! -
            dialogRef?.current?.offsetWidth!;

        const safeX = Math.max(minX, Math.min(coordinates.x, maxX));
        const safeY = Math.max(minY, Math.min(coordinates.y, maxY));
        return (
            coordinates
                ? {
                      "--top": `${safeY}px`,
                      "--left": `${safeX}px`,
                      "--right": "auto",
                      "--transform": "none",
                  }
                : {}
        ) as React.CSSProperties;
    };

    const modalContents = (
        <>
            {activeEditHighlightModal === editType && can(PageAction.Edit) ? (
                <CodeReferenceHighlightEditModalContents
                    existingHighlight={activeHighlight}
                    onClose={handleCloseEditor}
                    onSave={handleOnSaveClick}
                    onColorChange={onColorChange}
                />
            ) : (
                <div className={`${CSS_CLASS_NAME}__wrapper`}>
                    {showToggles && (
                        <nav className={`${CSS_CLASS_NAME}__toggles`}>
                            <Button
                                disabled={!canGoPrev}
                                onClick={handlePreviousHighlight}
                                size={ButtonSizes.XSmall}
                                style={ButtonStyles.Secondary}>
                                <Icon type={Icons.ChevronLeft} />
                            </Button>
                            <Button
                                disabled={!canGoNext}
                                onClick={handleNextHighlight}
                                size={ButtonSizes.XSmall}
                                style={ButtonStyles.Secondary}>
                                <Icon type={Icons.ChevronRight} />
                            </Button>
                            <Paragraph
                                size={ParagraphSizes.XSmall}
                                cssClassName={`${CSS_CLASS_NAME}__count`}>
                                {(activeHighlightIndex ?? 0) + 1} of{" "}
                                {highlightCount}
                            </Paragraph>
                        </nav>
                    )}
                    {activeHighlight && (
                        <>
                            <header className={`${CSS_CLASS_NAME}__header`}>
                                <Icon
                                    type={Icons.Highlighter}
                                    cssClassName={`${CSS_CLASS_NAME}__headerIcon -${HighlightColor[activeHighlight.highlightColor].toLowerCase()}`}
                                />
                                <span
                                    className={`${CSS_CLASS_NAME}__headerText`}>
                                    {highlighedText}
                                </span>
                                <Button
                                    size={ButtonSizes.Small}
                                    cssClassName={`${CSS_CLASS_NAME}__closeButton`}
                                    style={ButtonStyles.Secondary}
                                    onClick={onClose}>
                                    {t("close")}
                                </Button>
                            </header>
                            <section className={`${CSS_CLASS_NAME}__body`}>
                                <div className={`${CSS_CLASS_NAME}__note`}>
                                    <RichTextArea
                                        cssClassName={`${CSS_CLASS_NAME}__richTextArea`}
                                        content={note}
                                    />
                                </div>
                                <Paragraph
                                    cssClassName={`${CSS_CLASS_NAME}__timestamp`}
                                    size={ParagraphSizes.XXSmall}>
                                    {timestamp}
                                </Paragraph>
                            </section>
                            {can(PageAction.Edit) && (
                                <footer className={`${CSS_CLASS_NAME}__footer`}>
                                    <Button
                                        size={ButtonSizes.Small}
                                        onClick={
                                            handleDeleteConfirmationModalOpen
                                        }
                                        style={ButtonStyles.Tertiary}>
                                        {t("delete")}
                                    </Button>
                                    <Button
                                        size={ButtonSizes.Small}
                                        onClick={handleEditClick}
                                        style={ButtonStyles.Primary}>
                                        {t("editNote")}
                                    </Button>
                                </footer>
                            )}
                        </>
                    )}
                </div>
            )}

            <ConfirmationModal
                isVisible={isDeleteConfirmationModalOpen}
                message={t("confirmationMessage", {
                    action: t("delete").toLowerCase(),
                    item: t("highlight"),
                })}
                onCancel={handleDeleteConfirmationModalClose}
                onConfirm={handleConfirmDelete}
            />
        </>
    );

    return (
        <>
            {isMouseDevice ? (
                <dialog
                    ref={dialogRef}
                    className={CSS_CLASS_NAME}
                    style={styles()}>
                    {modalContents}
                </dialog>
            ) : (
                <Modal
                    type={ModalTypes.Bottom}
                    closeDialog={onClose}
                    label={""}
                    isVisible={true}>
                    {modalContents}
                </Modal>
            )}
        </>
    );
};

export default CodeReferenceHighlightViewModal;
